import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { toast } from 'sonner';
import { ArrowLeft, Check, Pencil, Send, Trash2, X } from 'lucide-react';
import SprekersLayout from '@/components/SprekersLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { sprekersApi } from '../api';
import EngagementFormDialog from '../components/EngagementFormDialog';
import InviteDialog from '../components/InviteDialog';
import ContentTab from '../components/ContentTab';
import NotesTab from '../components/NotesTab';
import PresentationTab from '../components/PresentationTab';
import useSpeakerProfiles from '../hooks/useSpeakerProfiles';
import { ENGAGEMENT_STATUSES, INVITATION_STATUSES, findClassName, findLabel } from '../labels';
import type { SpeakerInvitationInput, SpeakingEngagement, SpeakingEngagementInput } from '../types';

const RESPONDABLE_INVITATION_STATUSES = new Set(['verstuurd', 'bekeken', 'voorlopig_geaccepteerd']);

export default function EngagementDetailPage() {
  const { engagementId } = useParams<{ engagementId: string }>();
  const navigate = useNavigate();
  const { user, hasCapability, hasRole } = useAuth();
  const canManage = hasCapability('speakers_manage');
  const profiles = useSpeakerProfiles();

  const [engagement, setEngagement] = useState<SpeakingEngagement | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [responding, setResponding] = useState(false);

  const fetchEngagement = async () => {
    if (!engagementId) return;
    try {
      const data = await sprekersApi.getEngagement(engagementId);
      setEngagement(data);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEngagement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId]);

  const ownSpeakerProfileId = useMemo(
    () => profiles.find((p) => p.user_id === user?.id)?.id,
    [profiles, user?.id],
  );
  const isInvitedSpeaker = !!engagement && !!ownSpeakerProfileId && (
    engagement.speaker_id === ownSpeakerProfileId || engagement.co_speaker_ids.includes(ownSpeakerProfileId)
  );
  const canEditContent = isInvitedSpeaker || hasRole('admin');
  const canRespond = engagement ? (isInvitedSpeaker || canManage) && RESPONDABLE_INVITATION_STATUSES.has(engagement.invitation_status) : false;
  const speakerName = engagement ? profiles.find((p) => p.id === engagement.speaker_id)?.display_name || 'Onbekend' : '';

  const handleUpdate = async (data: SpeakingEngagementInput) => {
    if (!engagementId) return;
    try {
      const updated = await sprekersApi.updateEngagement(engagementId, data);
      setEngagement(updated);
      toast.success('Spreekbeurt bijgewerkt');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Bijwerken mislukt');
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!engagementId || !engagement) return;
    if (!window.confirm('Weet je zeker dat je deze spreekbeurt wilt verwijderen?')) return;
    try {
      await sprekersApi.deleteEngagement(engagementId);
      toast.success('Spreekbeurt verwijderd');
      navigate('/sprekers');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const handleInvite = async (data: SpeakerInvitationInput) => {
    if (!engagementId) return;
    try {
      const updated = await sprekersApi.inviteSpeaker(engagementId, data);
      setEngagement(updated);
      toast.success('Uitnodiging verstuurd');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Uitnodigen mislukt');
      throw error;
    }
  };

  const handleAccept = async () => {
    if (!engagementId) return;
    setResponding(true);
    try {
      const updated = await sprekersApi.acceptInvitation(engagementId, window.prompt('Eventuele toelichting (optioneel):') || undefined);
      setEngagement(updated);
      toast.success('Uitnodiging geaccepteerd');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Accepteren mislukt');
    } finally {
      setResponding(false);
    }
  };

  const handleDecline = async () => {
    if (!engagementId) return;
    setResponding(true);
    try {
      const updated = await sprekersApi.declineInvitation(engagementId, window.prompt('Reden (optioneel):') || undefined);
      setEngagement(updated);
      toast.success('Uitnodiging afgewezen');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Afwijzen mislukt');
    } finally {
      setResponding(false);
    }
  };

  if (!loading && (notFound || !engagement) && engagementId) {
    return (
      <SprekersLayout>
        <Card><CardContent className="p-6 text-sm text-slate-500">Spreekbeurt niet gevonden.</CardContent></Card>
      </SprekersLayout>
    );
  }

  return (
    <SprekersLayout>
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => navigate('/sprekers')}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        Terug naar spreekbeurten
      </Button>

      {engagement && (
        <>
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900">{engagement.title || engagement.working_title || 'Zonder titel'}</h2>
                <Badge className={findClassName(ENGAGEMENT_STATUSES, engagement.status)}>
                  {findLabel(ENGAGEMENT_STATUSES, engagement.status)}
                </Badge>
                <Badge className={findClassName(INVITATION_STATUSES, engagement.invitation_status)}>
                  {findLabel(INVITATION_STATUSES, engagement.invitation_status)}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-slate-500">
                {speakerName} &middot;{' '}
                {format(parseISO(engagement.start_at), "d MMMM yyyy 'om' HH:mm", { locale: nl })}
                {engagement.theme ? ` · ${engagement.theme}` : ''}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {canRespond && (
                <>
                  <Button size="sm" onClick={handleAccept} disabled={responding}>
                    <Check className="mr-2 h-4 w-4" />
                    Accepteren
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDecline} disabled={responding}>
                    <X className="mr-2 h-4 w-4" />
                    Afwijzen
                  </Button>
                </>
              )}
              {canManage && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setInviteOpen(true)}>
                    <Send className="mr-2 h-4 w-4" />
                    Uitnodigen
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Bewerken
                  </Button>
                  <Button variant="ghost" size="icon" onClick={handleDelete} aria-label="Verwijderen">
                    <Trash2 className="h-4 w-4 text-slate-400" />
                  </Button>
                </>
              )}
            </div>
          </div>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <Tabs defaultValue="voorbereiding">
                <TabsList>
                  <TabsTrigger value="voorbereiding">Voorbereiding</TabsTrigger>
                  <TabsTrigger value="presentatie">Presentatie</TabsTrigger>
                  <TabsTrigger value="notities">Privé-notities</TabsTrigger>
                </TabsList>
                <TabsContent value="voorbereiding" className="pt-4">
                  <ContentTab engagementId={engagement.id} currentUserId={user?.id} canEdit={canEditContent} />
                </TabsContent>
                <TabsContent value="presentatie" className="pt-4">
                  <PresentationTab engagementId={engagement.id} canEdit={canEditContent} />
                </TabsContent>
                <TabsContent value="notities" className="pt-4">
                  <NotesTab engagementId={engagement.id} currentUserId={user?.id} canEdit={canEditContent} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <EngagementFormDialog open={editOpen} onOpenChange={setEditOpen} engagement={engagement} onSubmit={handleUpdate} />
          <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} engagement={engagement} onSubmit={handleInvite} />
        </>
      )}
    </SprekersLayout>
  );
}
